import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GatherApiService } from '../gather-services/gather-api.service';
import { SharedDataService } from '../gather-services/shared-data.service';
import { Gather, JobPosting } from '../gather-typings';
import { Subscription } from 'rxjs';
import { GatherStateService } from '../gather-services/gather-state.service';
import { EnvironmentToken } from '../../../util';
import { Environment } from '../../../typings';
import { TranslateService } from '@ngx-translate/core';
import { Country } from '../enums';

@Component({
    selector: 'app-gather-landing-page-component',
    templateUrl: './gather-landing-page.component.html',
    styleUrls: ['./gather-landing-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GatherLandingPageComponent implements OnInit, OnDestroy {
  trackingId: string = null;
  jobPosting: JobPosting;
  gather: Gather;
  fromTextMessage: boolean;
  preferredName: string;
  placementType: string;
  alreadyResponded: boolean;
  hideForJapanOrder: boolean;
  payFrequencyWord: string;

  @ViewChild('preferencesLink', { static: false }) insertTarget: ElementRef;

  private subscriptions = new Subscription();

  readonly PLACEMENT_TYPE: string[] = ['Temp-to-Perm', 'Permanent', 'Temporary', 'Project'];

  constructor(
    private gatherStateService: GatherStateService,
    private gatherApiService: GatherApiService,
    private sharedDataService: SharedDataService,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    @Inject(EnvironmentToken) private environment: Environment
  ) {}

  ngOnInit(): void {
    this.fromTextMessage =
      this.route.snapshot.queryParamMap.has('textMessage') && this.route.snapshot.queryParamMap.get('textMessage') === 'true';

    // set gather subscription
    this.subscriptions.add(
      this.sharedDataService.gather.subscribe(gather => {
        this.gather = gather;
        this.preferredName = this.gather.talent.firstName;

        if (this.gather.countryCode === 'JP') {
          this.preferredName = this.gather.talent.lastName;
        }
      })
    );

    // set job posting
    this.subscriptions.add(
      this.sharedDataService.jobPosting.subscribe(posting => {
        this.jobPosting = posting;
        this.hideForJapanOrder = posting.countryId !== Country.Japan;
        if (this.jobPosting.placementTypeId && this.jobPosting.placementTypeId <= 4) {
          this.placementType = this.PLACEMENT_TYPE[this.jobPosting.placementTypeId - 1];
        }
        if (this.jobPosting.payTransparencyCategoryId) {
          this.payFrequencyWord = this.choosePayFrequencyWord(this.jobPosting.payTransparencyCategoryId);
        }
      })
    );

    // set email_tracking_id uuid
    this.subscriptions.add(
      this.sharedDataService.trackingId.subscribe(id => {
        this.trackingId = id;
      })
    );
    // set alreadyResponded
    this.subscriptions.add(
      this.sharedDataService.talentAvailability.subscribe(talentAvailability => {
        this.alreadyResponded = talentAvailability.alreadyResponded;
      })
    );

    // set the title
    this.gatherStateService.setTitle('gather.titles.role-interest');
  }

  goToInterested() {
    this.router.navigate(['gather', this.trackingId, 'availability'],
      {queryParams: {'alreadyResponded': this.alreadyResponded, 'textMessage': this.fromTextMessage}}
    );
  }

  goToMat(): void {
    const url = this.environment.matHost;
    window.open(url, '_blank');
  }

  goToNotInterested(): void {
    void this.router.navigate(['gather', this.trackingId, 'not-interested'], { queryParams: { textMessage: this.fromTextMessage } });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  choosePayFrequencyWord(payTransparencyCategoryId: number): string {
    const choiceMap = {
      1: 'hour',
      2: 'day',
      3: 'year',
      4: 'project'
    }

    return this.translateService.instant(`pay-unit.${choiceMap[payTransparencyCategoryId]}-abbreviated`);
  }
}
